/**
 * Decides whether a restored reading position can open the book, before the
 * navigator sees it.
 *
 * Readium's FramePoolManager.update() trusts `locations.position` over the href
 * and throws when the number is past the list, and apply() throws when the href
 * is not in the reading order. Either rejection leaves the reader on its spinner.
 * Inputs are plain JSON: the host's saved locator or the legacy parser's result.
 * A Locator the store kept from an earlier position change is read through
 * serialize().
 *
 * @param {Object|undefined} saved - Restored position
 * @param {Object} coverage - What the publication can open
 * @param {string[]} coverage.readingOrderHrefs
 * @param {Array<{ href: string, locations: { position?: number } }>} coverage.positions
 * @returns {{ locator: Object|null, reason?: string }} The locator to open, null when the
 *   publication does not cover it, with the reason behind a repair or a refusal
 */
export function resolveInitialLocator(saved, { readingOrderHrefs, positions }) {
    const locator = typeof saved?.serialize === 'function' ? saved.serialize() : saved;
    if (!locator?.href) return rejected('missing_href');

    // The reading order lists hrefs without their fragment.
    const href = locator.href.split('#')[0];
    if (!readingOrderHrefs.includes(href)) return rejected('href_not_in_reading_order');
    // Without a list the navigator fetches one in load(), so there is nothing to check against.
    if (!positions?.length) return { locator };

    const entry = positions.find(position => position.href === href);
    if (!entry) return rejected('href_without_positions');

    const reason = repairReason(locator.locations?.position, positions, href);
    if (!reason) return { locator };

    // Any entry of the chapter opens the same frame, and show() then lands on
    // the saved progression, so the first one is as good as a nearer one.
    return {
        locator: { ...locator, locations: { ...locator.locations, position: entry.locations.position } },
        reason,
    };
}

function repairReason(position, positions, href) {
    if (!Number.isInteger(position)) return 'position_missing';
    if (position < 1 || position > positions.length) return 'position_out_of_range';
    if (positions[position - 1].href !== href) return 'position_href_mismatch';
    return null;
}

function rejected(reason) {
    return { locator: null, reason };
}
